import { useQuery, useApolloClient } from '@apollo/client';
import { useMemo } from 'react';
import { FETCH_SITE_PERMISSIONS } from '../graphql/query/site';
import { hasPlatformFeature } from '../utils';

interface UseFetchPermissionsProps {
  siteId?: number | string | undefined;
  key?: string;
  subkey?: string;
}

export function useFetchPermissions({ siteId, key, subkey }: UseFetchPermissionsProps) {
  const client = useApolloClient();
  const cacheData = useMemo(() => {
    if (!hasPlatformFeature('user_roles') || !siteId || window?.isSiteOwner === true) return null;
    try {
      const cached = client.readQuery({
        query: FETCH_SITE_PERMISSIONS,
        variables: { id: Number(siteId) },
      });
      return cached?.site?.permissions ?? null;
    } catch {
      return null;
    }
  }, [client, siteId, window?.isSiteOwner]);

  // If cacheData is present, use it; else run network query
  const skipNetwork = !hasPlatformFeature('user_roles') || !!cacheData || !siteId || window?.isSiteOwner === true;
  const {
    data: { site = {} } = {},
    loading: permissionLoading,
    error,
    refetch,
  } = useQuery(FETCH_SITE_PERMISSIONS, {
    variables: { id: Number(siteId) },
    skip: skipNetwork,
    fetchPolicy: 'network-only',
  });
  
  const permissions = site?.permissions ?? null;
  const effectivePermissions = cacheData || permissions;

  const result = useMemo(() => {
    if (effectivePermissions === null) return null;
    if (key && !subkey) {
      return effectivePermissions[key] ?? null;
    }
    if (key && subkey) {
      return effectivePermissions[key]?.[subkey] ?? null;
    }
    return effectivePermissions;
  }, [effectivePermissions, key, subkey]);

  return useMemo(
    () => ({
      permissions: result,
      permissionLoading,
      error,
      refetch,
    }),
    [result, permissionLoading, error, refetch]
  );
}